<template>
    <div 
        :class="{
            'filter':true, 
            'filter--expanded':expanded,
        }" 
        tabindex="-1" 
        @mouseleave="expanded = false"
        @keydown.esc.prevent="closeCombo"
        @keydown.tab="closeCombo"
        @keydown.up.down.prevent="handleFocus"
    >
        <button 
            :id="`${labelOptions}-btn`"
            ref="combo"
            role="combobox"
            class="filter__label" 
            aria-haspopup="listbox"
            :aria-controls="`${labelOptions}-box`"
            :aria-activedescendant="selectedOption || undefined"
            :aria-expanded="expanded.toString()"
            :aria-label="selectedOption ? `Filter by ${selectedOption}` : label"
            @click="(e) => {
                !expanded ? handleFocus({ key: 'ArrowDown' }) : toggleCombo(e)
            }"
        >
            <span v-text="selectedOption ? selectedOption : label"></span>
        </button>
        
        <ul 
            :id="`${labelOptions}-box`"
            ref="comboList"
            role="listbox" 
            class="filter__options" 
            :aria-labelledby="`${labelOptions}-btn`"
            :aria-hidden="(!expanded).toString()"
        >
            <li
                :id="`${labelOptions}-${option.id}`"
                class="filter__option"
                v-for="option in options"
                role="option" tabindex="-1"
                :aria-selected="(selected === option.id).toString()"
                :key="option.id"
                @click="select(option.id)"
                @keydown.enter.space.prevent="select(option.id)"
            >
                <span class="filter__color-label" v-if="option.color" :style="{backgroundColor: option.color}"></span>
                <span class="option" v-text="option.name"></span>
            </li>
        </ul>

        <div 
            :aria-label="`Clear ${label}`" 
            :tabindex="expanded ? -1 : 0" 
            role="button" ref="clear"
            class="filter__btn-clear" 
            @click="clearSelection" 
            @keyup.enter="clearSelection"
            @keydown.tab.stop
            v-show="selectedOption"
        >
            <span>&times;</span>
        </div>

        <div class="filter__btn-expand" @click="toggleCombo">
            <arrow-button tag="span" type="bottom"></arrow-button>
        </div>
    </div>
</template>

<script>
export default {
    name: "FilterBy",
    props: {
        options: {
            type: Array
        },
        label: {
            type: String,
            default: ''
        },
        value: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            selected: null,
            expanded: false
        }
    },

    computed: {
        labelOptions() {
            if (!this.label || !this.label.includes(' ')) {
                return this.label
            }
            const arr = this.label.split(' ')
            return arr.length > 2 && `${arr[2]}s`
        },
        selectedOption() {
            const option = this.options
                ? this.options.filter(option => option.id === this.selected)
                : []
            if (option.length === 1)
                return option[0].name

            return null
        }
    },

    watch: {
        selected() {
            this.expanded = false
        },
    },

    methods: {
        handleFocus(event) {
            const { key } = event
            
            const optionsList = [...this.$refs.comboList.querySelectorAll(`[id^=${this.labelOptions}]`)]
            const focusableItem = optionsList.find(el => el.id.includes(this.selected)) || optionsList[0]
            const focusableItemIndex = optionsList.indexOf(document.activeElement)

            const [ firstEl, lastEl ] = [
                optionsList[0], 
                optionsList[optionsList.length-1]
            ]

            if ( key === 'ArrowDown' ) {

                if ( focusableItemIndex < 0 ) {
                    !this.expanded && this.toggleCombo()
                    
                    return setTimeout(() => focusableItem.focus(), 100)
                }
                
                if (focusableItemIndex !== optionsList.length-1) {
                    return optionsList[focusableItemIndex+1].focus()
                }
                
                firstEl.focus()
            }

            else if ( key === 'ArrowUp' ) {

                if ( focusableItemIndex < 0 ) {
                    !this.expanded && this.toggleCombo()

                    return setTimeout(() => focusableItem.focus(), 100)
                }

                if ( focusableItemIndex > 0 ) {
                    return optionsList[focusableItemIndex-1].focus()
                }
                
                lastEl.focus()
            }
        },
        select(optionId) {
            this.$emit('input', optionId)
            this.selected = optionId
            this.$refs.combo.focus()
        },
        closeCombo() {
            this.expanded = false
            this.$refs.combo.focus()
        },
        toggleCombo() {
            this.expanded = !this.expanded
        },
        clearSelection() {
            this.$emit('input', null)
            this.selected = null
            
            this.$refs.combo.focus()
        },
    }
}
</script>

<style lang="scss">
.filter {
    --fh: 46px;
    --mw: 100%;
    position: relative;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    width: var(--mw);
    height: var(--fh);
    background-color: #fff;
    border-radius: 2em;
    display: inline-flex;

    @media (min-width: 768px) {
        --mw: 300px;
    }

    &::after {
        content: '';
        position: absolute;
        pointer-events: none;
        border-radius: inherit;
        inset: 0;
        border: 1px solid #522F8B;
    }

    &--expanded {
        
        .filter__label,
        .filter__btn-expand,
        .filter__btn-clear {
            z-index: 30;
        }
        .filter__options {
            max-height: 340px;
            z-index: 20;
        }
    }
}

.filter:focus-within {
    box-shadow: 0 0 12px 1px rgba(purple, .25);
}

.filter__label:hover,
.filter:focus-within .filter__label {
    background-color: mix(black, white, 6%);
}

[aria-expanded="true"] ~ .filter__btn-expand svg {
    transform: translate3d(-50%, -50%, 0) rotate(180deg);
}

.filter__label {
    position: absolute;
    inset: .35rem;
    text-indent: 1rem;
    text-align: left;
    border-radius: 1rem;
    box-shadow: none;
    background: none;
    flex: 1 0 auto;
    outline: none;
    cursor: pointer;
    transition: background-color .2s;
}

.filter__btn-clear {
    $s: 16px;
    position: absolute;
    display: flex;
    top: calc(50% - #{$s/2});
    right: 40px;
    height: $s;
    aspect-ratio: 1/1;
    text-align: center;
    cursor: pointer;
    border-radius: 9em;

    > * {
        position: relative;
        top: -.3rem;
        font-size: 24px;
        line-height: 1;
        margin: auto;
    }

    &:focus-visible {
        outline: 2px dashed currentColor;
    }
}

.filter__btn-expand {
    flex-basis: 20px;
    align-self: center;
    margin-right: .25em;
    margin-left: auto;
    cursor: pointer;

    .link {
        padding-top: 0;
    }
}

.filter__label,
.filter__btn-clear,
.filter__btn-expand {
    z-index: 15;
}

.filter__options {
    overflow: hidden;
    backface-visibility: hidden;
    position: absolute;
    flex-basis: 100%;
    max-height: 0px;
    left: 0;
    right: 0;
    top: calc(var(--fh) * .5);
    padding: calc(var(--fh) * .25 - 1px) 15px;
    z-index: 10;
    background: #fff;
    border: {
        left: 1px solid #522F8B;
        right: 1px solid #522F8B;
        bottom: 1px solid #522F8B;
    }
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
    transition: max-height .5s var(--timing);

    > :first-child {
        margin-top: 15px;
    }
    > :last-child {
        margin-bottom: 5px
    }
}

.filter--expanded .filter__option {
    opacity: 1;
    transition: opacity .2s;
    @for $i from 1 through 20 {
        &:nth-of-type(#{$i}) {
            transition-duration: .2s * ($i * .5);
        }
    }
}

.filter__option {
    opacity: 0;
    cursor: pointer;

    &:hover,
    &[aria-selected=true] {
        color: var( --purple );

        .filter__color-label {
            width: 10px;
        }
    }

    &:focus-visible {
        outline: 2px dashed currentColor;
        outline-offset: .1rem;
    }

    .filter__color-label {
        border-radius: 9em;
        transform: scale3d(1, 1, 1);
        transition: width .2s;
        transform-origin: left;
        display: inline-block;
        width: 3px;
        height: 10px;
        margin-right: 10px;
    }
}
</style>
